import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CircleCheck, Zap, Layers, FlaskConical, Droplets, Sun, Gem, Sparkles } from 'lucide-react';
import { useScrollReveal, useCountUp } from '../hooks/useAnimations';
import { useQuote } from '../context/QuoteContext';

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`transition-all duration-[800ms] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const StatHighlight: React.FC<{ value: string; label: string; suffix?: string }> = ({ value, label, suffix }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  const num = parseFloat(value);
  const count = useCountUp(isNaN(num) ? 0 : num, 2000, visible);
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl sm:text-5xl font-extrabold text-gradient">
        {isNaN(num) ? value : count.toFixed(value.includes('.') ? 2 : 0)}
        {suffix}
      </div>
      <div className="text-sm text-silver-500 mt-2 font-medium">{label}</div>
    </div>
  );
};

const stages = [
  {
    num: '01',
    title: 'Ozonation AOP',
    subtitle: 'Advanced Oxidation Process',
    description: 'Powerful ozone injection breaks down organic and inorganic impurities at the molecular level using advanced oxidation.',
    icon: <Zap className="w-5 h-5 text-white" />,
    color: 'from-violet-500 to-purple-600',
    bg: 'from-violet-50 to-purple-50',
  },
  {
    num: '02',
    title: 'Pressure Sand Filtration',
    subtitle: 'Multi-Grade Media Filter',
    description: 'Water passes through graded silica sand layers, removing suspended particles, turbidity, and sediment down to micron levels.',
    icon: <Layers className="w-5 h-5 text-white" />,
    color: 'from-amber-500 to-orange-600',
    bg: 'from-amber-50 to-orange-50',
  },
  {
    num: '03',
    title: 'Activated Carbon',
    subtitle: 'Adsorption Filtration',
    description: 'Granular activated carbon adsorbs chlorine, volatile organics, odor, and taste-causing compounds for crystal-clear water.',
    icon: <FlaskConical className="w-5 h-5 text-white" />,
    color: 'from-silver-600 to-silver-800',
    bg: 'from-silver-100 to-silver-200',
  },
  {
    num: '04',
    title: 'Reverse Osmosis',
    subtitle: 'Semi-Permeable Membrane',
    description: 'Industrial RO membranes remove dissolved salts, heavy metals, and contaminants with 99%+ rejection rates.',
    icon: <Droplets className="w-5 h-5 text-white" />,
    color: 'from-brand-500 to-brand-700',
    bg: 'from-brand-50 to-blue-50',
  },
  {
    num: '05',
    title: 'UV + Micron Filtration',
    subtitle: 'Dual Disinfection',
    description: 'UV irradiation eliminates bacteria and viruses while micron filters catch any remaining particulates.',
    icon: <Sun className="w-5 h-5 text-white" />,
    color: 'from-sky-500 to-cyan-600',
    bg: 'from-sky-50 to-cyan-50',
  },
  {
    num: '06',
    title: 'Mineral Enrichment',
    subtitle: 'Essential Mineral Addition',
    description: 'Precise re-mineralization adds calcium, magnesium, and essential minerals for optimal taste and health benefits.',
    icon: <Gem className="w-5 h-5 text-white" />,
    color: 'from-emerald-500 to-teal-600',
    bg: 'from-emerald-50 to-teal-50',
  },
  {
    num: '07',
    title: 'Final Ozonation',
    subtitle: 'Packaging-Stage Disinfection',
    description: 'A second ozone dose ensures bottle-level sterility. Ozone dissipates naturally, leaving no chemical residue.',
    icon: <Sparkles className="w-5 h-5 text-white" />,
    color: 'from-brand-600 to-indigo-700',
    bg: 'from-brand-50 to-indigo-50',
  },
];

const Science: React.FC = () => {
  const { openQuote } = useQuote();
  return (
    <main className="bg-white overflow-hidden">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 pb-14 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-silver-50 via-brand-50/30 to-white" />
        <div className="absolute inset-0 dot-grid opacity-[0.3]" />
        <div className="absolute top-20 left-[10%] w-[500px] h-[500px] bg-brand-200/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-10 right-[10%] w-[350px] h-[350px] bg-violet-200/15 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5 animate-fade-in">Our Science</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-silver-950 tracking-tight leading-[1.1] animate-slide-up">
            7-Stage <span className="text-gradient">Multi-Barrier</span> Purification
          </h1>
          <p className="mt-7 text-lg text-silver-500 max-w-2xl mx-auto leading-relaxed animate-slide-up-delayed">
            Every drop undergoes seven scientifically engineered stages — from advanced oxidation to final ozonation — ensuring water that isn't just safe, but exceptionally pure.
          </p>
        </div>
      </section>

      {/* ═══ PROCESS TIMELINE ═══ */}
      <section className="py-16 lg:py-20 bg-white relative">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">The Process</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                From Source to Seal
              </h2>
            </div>
          </Reveal>

          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-brand-200 via-brand-300 to-brand-200 hidden sm:block" />

            <div className="space-y-8 lg:space-y-12">
              {stages.map((stage, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <Reveal key={stage.num} delay={i * 80}>
                    <div className={`relative flex flex-col lg:flex-row items-start gap-6 lg:gap-12 ${!isLeft ? 'lg:flex-row-reverse' : ''}`}>
                      {/* Timeline dot */}
                      <div className="hidden sm:flex absolute left-6 lg:left-1/2 -translate-x-1/2 z-10">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stage.color} shadow-lg flex items-center justify-center text-lg`}>
                          {stage.icon}
                        </div>
                      </div>

                      {/* Content card */}
                      <div className={`sm:ml-20 lg:ml-0 lg:w-[calc(50%-3rem)] ${!isLeft ? 'lg:mr-auto' : 'lg:ml-auto'}`}>
                        <div className="group bg-white border border-silver-100 rounded-2xl p-7 hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 gradient-border">
                          <div className="flex items-start gap-4">
                            <div className={`sm:hidden w-10 h-10 rounded-xl bg-gradient-to-br ${stage.color} shadow-md flex items-center justify-center text-base flex-shrink-0`}>
                              {stage.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`text-xs font-black bg-gradient-to-r ${stage.color} bg-clip-text text-transparent`}>{stage.num}</span>
                                <span className="text-[10px] text-silver-400 uppercase tracking-wider font-medium">{stage.subtitle}</span>
                              </div>
                              <h3 className="text-lg font-bold text-silver-900 mb-2">{stage.title}</h3>
                              <p className="text-sm text-silver-500 leading-relaxed">{stage.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 7-STAGE PROCESS VIDEO — FULL WIDTH ═══ */}
      <section className="relative overflow-hidden">
        <div className="relative h-[85vh] min-h-[500px]">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            src="/assets/videos/Aerohix-water-video.mp4"
          />
          {/* Multi-layer overlay for cinematic depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-950/80 via-brand-900/60 to-brand-800/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-950/70 via-transparent to-transparent" />
          {/* Decorative rings */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border border-white/[0.06] rounded-full" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] border border-white/[0.03] rounded-full" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Reveal>
              <div className="text-center px-6 max-w-3xl mx-auto">
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-300 bg-white/10 backdrop-blur-sm border border-white/10 px-4 py-1.5 rounded-full mb-6">
                  The Arohix Process
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                  7 Stages of{' '}
                  <span className="bg-gradient-to-r from-brand-300 via-sky-300 to-brand-200 bg-clip-text text-transparent">
                    Engineered Purity
                  </span>
                </h2>
                <p className="mt-5 text-brand-100/70 text-lg leading-relaxed max-w-xl mx-auto">
                  From source water to sealed bottle — every step is precision-engineered, fully automated, and relentlessly tested.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                  {['Ozonation AOP', 'Mineral Enrichment', 'UV Sterilisation', 'Zero Human Touch'].map((tag) => (
                    <span key={tag} className="text-[11px] font-semibold text-white/80 bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ WHY IT MATTERS - STATS ═══ */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-silver-50/50 to-white relative">
        <div className="absolute inset-0 dot-grid opacity-[0.2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Why It Matters</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                The Numbers Speak
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <Reveal delay={0}>
              <div className="bg-white border border-silver-100 rounded-2xl p-8 text-center hover:shadow-card-hover hover:-translate-y-1 transition-all duration-500 gradient-border">
                <StatHighlight value="99.99" suffix="%" label="Pathogen Elimination" />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="bg-white border border-silver-100 rounded-2xl p-8 text-center hover:shadow-card-hover hover:-translate-y-1 transition-all duration-500 gradient-border">
                <StatHighlight value="7" suffix="+" label="Purification Stages" />
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="bg-white border border-silver-100 rounded-2xl p-8 text-center hover:shadow-card-hover hover:-translate-y-1 transition-all duration-500 gradient-border">
                <StatHighlight value="2" suffix="×" label="Ozone Treated" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ COMPARISON TABLE ═══ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Comparison</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                How Arohix <span className="text-gradient">Compares</span>
              </h2>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="bg-white border border-silver-100 rounded-2xl overflow-hidden shadow-card">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-brand-600 to-brand-700 text-white">
                      <th className="text-left py-4 px-6 font-semibold text-xs uppercase tracking-wider">Parameter</th>
                      <th className="text-center py-4 px-6 font-semibold text-xs uppercase tracking-wider">Standard Water</th>
                      <th className="text-center py-4 px-6 font-semibold text-xs uppercase tracking-wider bg-brand-800/30">
                        <span className="flex items-center justify-center gap-1.5">✦ Arohix Water</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { param: 'Purification Stages', standard: '2–3', arohix: '7 stages' },
                      { param: 'Ozone Treatment', standard: 'Single / None', arohix: 'Dual Ozonation' },
                      { param: 'Mineral Enrichment', standard: 'Not always', arohix: 'Yes — Calibrated' },
                      { param: 'Human Contact', standard: 'Possible', arohix: 'Zero Touch' },
                      { param: 'pH Range', standard: '6.5–7.0', arohix: '7.2–7.5' },
                      { param: 'TDS Management', standard: 'Inconsistent', arohix: 'Precision Controlled' },
                      { param: 'Certifications', standard: 'Varies', arohix: 'ISO + FSSAI + BIS + WHO' },
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-silver-100 ${i % 2 === 0 ? 'bg-white' : 'bg-silver-50/30'} hover:bg-brand-50/30 transition-colors duration-200`}>
                        <td className="py-4 px-6 font-medium text-silver-800">{row.param}</td>
                        <td className="py-4 px-6 text-center text-silver-400">{row.standard}</td>
                        <td className="py-4 px-6 text-center font-semibold text-brand-600 bg-brand-50/20">
                          <span className="inline-flex items-center gap-1.5">
                            <CircleCheck className="w-4 h-4 text-emerald-500" />
                            {row.arohix}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/[0.03] rounded-full" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Convinced by the Science?
            </h2>
            <p className="mt-6 text-lg text-brand-100/70 max-w-xl mx-auto leading-relaxed">
              Explore our products or get in touch to bring Arohix Water to your business.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/products" className="shimmer-btn group inline-flex items-center gap-2.5 bg-white text-brand-700 px-8 py-4 rounded-full text-sm font-bold hover:bg-brand-50 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]">
                View Products
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <button onClick={openQuote} className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors duration-300">
                Contact Us <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
};

export default Science;
